import { apiSlice } from '@/redux/api.slice';
import {
  logout,
  setUser,
  setTokens,
  updateNewsletter,
  setProfileCompletionLink,
  setIsFetchingProfile,
  setAnalytics
} from './auth.slice';
import Cookies from 'js-cookie';
import { DEAL_SHARE_COOKIE, parseDealShareCookie } from '@/lib/deal-share';
import { AuthResponse, NewsletterOption, UserInfoResponse } from './auth.type';
import { transformUserInfoData } from '@/lib/transform-response';
import { logQueryError } from '@/redux/log-query-error';
import {
  trackLoginSuccess,
  trackCompleteRegistration,
  trackRegistrationVerified,
  CompleteRegistrationProperties
} from '@/lib/posthog/posthog-events';
import { identifyUser } from '@/lib/posthog/posthog-user';
import { hasMarketingConsent } from '@/lib/consent/marketing-consent';
import { getFbc } from '@/lib/meta/fbc';
import { IdentityStatus } from './auth.type';

const VERIFIED_SENT_KEY = 'registrationVerifiedSent';

/**
 * Attribution for a signup that came through a shared deal link.
 *
 * Read here and nowhere else: `register`'s onQueryStarted removes the cookie as
 * soon as the signup succeeds, so this is the last point where the sharer and
 * the originating deal are still known client-side. Without it the funnel ends
 * at `referral_visit` — `utm_source=referral` alone expires with PostHog's
 * 30-minute UTM window and cannot be joined to the registration.
 */
const dealShareRegistrationProps =
  (): Partial<CompleteRegistrationProperties> => {
    const dealShare = parseDealShareCookie(Cookies.get(DEAL_SHARE_COOKIE));
    if (!dealShare) return {};

    return {
      referral_source: 'deal_share',
      referrer_code: dealShare.ref,
      offer_id: dealShare.deal,
      shop_id: dealShare.retailer
    };
  };

/**
 * Fire `registration_verified` once per user.
 *
 * Work identity reaches VERIFIED / MANUAL_VERIFIED through several paths, one
 * of which (manual admin approval) happens server-side with no client
 * involvement at all. So rather than trying to catch a transition the browser
 * often can't see, this fires the first time the client observes the user in a
 * verified state, deduped per analytics id.
 *
 * That makes the timestamp "first seen verified" rather than "moment of
 * verification" — fine for funnel membership, which is what it is used for,
 * but worth knowing before reading it as a latency metric.
 */
const trackVerifiedOnce = (analyticsId: string, status?: string): void => {
  if (
    status !== IdentityStatus.VERIFIED &&
    status !== IdentityStatus.MANUAL_VERIFIED
  ) {
    return;
  }
  if (typeof window === 'undefined') return;

  try {
    const sent = localStorage.getItem(VERIFIED_SENT_KEY);
    if (sent === analyticsId) return;
    localStorage.setItem(VERIFIED_SENT_KEY, analyticsId);
  } catch {
    // Storage blocked — better a possible duplicate than a missing conversion.
  }

  trackRegistrationVerified({ status });
};

const authTransformResponse = (response: { data: AuthResponse }) => {
  const { data } = response;
  const { userInfo, tokens, profileCompletionLink, analytics, metaEventId } =
    data;
  return {
    tokens,
    userInfo: transformUserInfoData(userInfo),
    profileCompletionLink,
    analytics,
    metaEventId
  };
};

const authOnQueryStarted = async (
  _arg: any,
  { queryFulfilled, dispatch }: any,
  options?: { trackLoginEvent?: boolean }
) => {
  try {
    const response = await queryFulfilled;
    if (response?.data?.tokens?.accessToken) {
      dispatch(setTokens(response?.data.tokens));
      dispatch(setUser(response?.data.userInfo));
      dispatch(setIsFetchingProfile(false));
      // Unconditional: a response without a link must clear any previous one,
      // which holds a token that logs the bearer in as its owner.
      dispatch(
        setProfileCompletionLink(response?.data?.profileCompletionLink ?? null)
      );
      // Set analytics data and identify user in PostHog
      if (response?.data?.analytics) {
        dispatch(setAnalytics(response.data.analytics));
        // Side effects live here, not in the reducer
        identifyUser(
          response.data.analytics.analytics_id,
          response.data.analytics.user_segment,
          response.data.analytics.verified_status,
          response.data.analytics.cio_marketable
        );
        if (options?.trackLoginEvent) {
          trackLoginSuccess();
        }
        trackVerifiedOnce(
          response.data.analytics.analytics_id,
          response.data.userInfo?.workIdentityVerificationStatus
        );
      }

      // Store Meta CAPI event ID in localStorage so the success page can
      // push it to GTM dataLayer for browser pixel deduplication
      // (localStorage persists across tabs — needed for email verification links)
      if (response?.data?.metaEventId && typeof window !== 'undefined') {
        if (hasMarketingConsent()) {
          try {
            localStorage.setItem('metaEventId', response.data.metaEventId);
          } catch {
            // localStorage unavailable (e.g. Safari private mode, restrictive iframes)
          }
        }
        trackCompleteRegistration({
          method: 'email',
          ...dealShareRegistrationProps()
        });
      }
    }
  } catch {}
};

export const authApi = apiSlice.injectEndpoints({
  // Next dev (Fast Refresh / on-demand entries) can re-evaluate this module
  // against the still-alive `apiSlice` singleton from an earlier compile,
  // which makes RTK Query warn about overriding endpoints that already
  // exist. Harmless in dev — the definitions are identical — so only
  // suppress it there; production keeps the default `false` so a real
  // duplicate endpoint name still warns.
  overrideExisting: process.env.NODE_ENV !== 'production',
  endpoints: (builder) => ({
    register: builder.mutation({
      query: (data) => {
        const body = { ...data };
        delete body.isAgreedToTermsAndConditions;
        const dealShare = parseDealShareCookie(Cookies.get(DEAL_SHARE_COOKIE));
        return {
          url: 'register',
          method: 'POST',
          body: {
            ...body,
            ...(dealShare ? { dealShare } : {}),
            fbp: Cookies.get('_fbp'),
            fbc: getFbc(),
            // The server sends Meta's CompleteRegistration only with this.
            marketingConsent: hasMarketingConsent()
          }
        };
      },
      transformResponse: authTransformResponse,
      onQueryStarted: async (_arg, api) => {
        await authOnQueryStarted(_arg, api);
        try {
          await api.queryFulfilled;
          // Recorded server-side now; keeping it would credit a second signup.
          Cookies.remove(DEAL_SHARE_COOKIE, { path: '/' });
        } catch {
          // Failed registration keeps the cookie so a retry stays attributable.
        }
      }
    }),
    completeProfile: builder.mutation({
      query: (data: Record<string, unknown>) => {
        return {
          url: 'complete-profile',
          method: 'POST',
          body: data
        };
      },
      transformResponse: authTransformResponse,
      onQueryStarted: async (_arg, api) => {
        await authOnQueryStarted(_arg, api);
      },
      invalidatesTags: ['TopDeals', 'HomeSlider']
    }),
    tryAgainCompleteProfile: builder.mutation({
      query: (data) => {
        return {
          url: 'try-again-complete-profile',
          method: 'POST',
          body: data
        };
      },
      // This resets workIdentityVerificationStatus server-side but returns only
      // the new token, so without this the store keeps the pre-reset status and
      // every CTA on the site keeps offering the step the user just left.
      invalidatesTags: ['Profile']
    }),
    verifyWorkEmail: builder.mutation({
      query: (data) => {
        return {
          url: 'verify-work-email',
          method: 'POST',
          body: data
        };
      },
      transformResponse: authTransformResponse,
      onQueryStarted: async (_arg, api) => {
        await authOnQueryStarted(_arg, api);
      },
      invalidatesTags: ['TopDeals', 'HomeSlider']
    }),
    changePassword: builder.mutation({
      query: (data) => {
        return {
          url: 'profile/password',
          method: 'POST',
          body: data
        };
      }
    }),
    login: builder.mutation({
      query: (data) => ({
        url: 'login',
        method: 'POST',
        body: data
      }),
      transformResponse: authTransformResponse,
      onQueryStarted: async (_arg, api) => {
        await authOnQueryStarted(_arg, api, { trackLoginEvent: true });
      },
      invalidatesTags: ['TopDeals', 'HomeSlider']
    }),
    logout: builder.mutation({
      query: () => ({
        url: 'logout',
        method: 'POST',
        body: {
          refreshToken: Cookies.get('refreshTokenUser')
        }
      }),
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const result = await queryFulfilled;

          if (result?.data?.status === 'success') {
            dispatch(logout());
            dispatch(apiSlice.util.resetApiState());
          }
        } catch {
          dispatch(logout());
          dispatch(apiSlice.util.resetApiState());
        }
      }
    }),
    resetPassword: builder.mutation({
      query: (data) => {
        return {
          url: 'reset-password',
          method: 'POST',
          body: data
        };
      }
    }),
    forgotPassword: builder.mutation({
      query: (data) => {
        return {
          url: 'forgot-password',
          method: 'POST',
          body: data
        };
      }
    }),
    getProfile: builder.query({
      query: () => {
        return {
          url: 'profile',
          method: 'GET'
        };
      },
      transformResponse: (response: { data: UserInfoResponse }) => {
        const { data } = response;
        return {
          user: transformUserInfoData(data),
          analytics: data.analytics ?? null,
          // A field the response never carried is not the same as one the
          // server sent empty: web and server deploy independently, so until
          // GET /profile returns this key, `undefined` means "no opinion" and
          // must leave whatever login stored alone.
          profileCompletionLink:
            'profileCompletionLink' in data
              ? (data.profileCompletionLink ?? null)
              : undefined
        };
      },
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const response = await queryFulfilled;
          if (response?.data?.user) {
            dispatch(setUser(response.data.user));
            dispatch(setIsFetchingProfile(false));
            // Not guarded on truthiness — an explicit null clears the link once
            // the profile is complete. Only a field the response omitted
            // entirely is ignored (see transformResponse).
            if (response.data.profileCompletionLink !== undefined) {
              dispatch(
                setProfileCompletionLink(response.data.profileCompletionLink)
              );
            }
          }
          if (response?.data?.analytics) {
            dispatch(setAnalytics(response.data.analytics));
            identifyUser(
              response.data.analytics.analytics_id,
              response.data.analytics.user_segment,
              response.data.analytics.verified_status,
              response.data.analytics.cio_marketable
            );
          }
        } catch (error: any) {
          if (error.error?.data?.status === 'error') {
            dispatch(logout());
          }
        }
      },
      providesTags: ['Profile']
    }),
    getReferralCode: builder.query({
      query: () => ({
        url: 'profile/referral-code',
        method: 'GET'
      }),
      providesTags: ['ReferralCode']
    }),
    updateNewsletterStatus: builder.mutation({
      query: (
        arg:
          | NewsletterOption
          | { status: NewsletterOption; method: 'newsletter_popup' }
      ) => {
        const { status, method } =
          typeof arg === 'string' ? { status: arg, method: undefined } : arg;
        return {
          url: `profile/newsletter/${status}`,
          method: 'PATCH',
          params: method ? { method } : undefined
        };
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const result = await queryFulfilled;

          if (result?.data?.status === 'success') {
            dispatch(updateNewsletter(result?.data?.data?.isAllowNewsLetter));
          }
        } catch (err) {
          logQueryError('newsletter update failed', err);
        }
      }
    }),
    getIndustryType: builder.query({
      query: () => {
        return {
          url: 'industry-type',
          method: 'GET'
        };
      }
    }),
    getIndustryInfo: builder.query({
      query: (token) => {
        return {
          url: `/industry-info/${token}`,
          method: 'GET'
        };
      }
    }),
    deleteAccount: builder.mutation({
      query: (currentPassword: string) => ({
        url: 'profile',
        method: 'DELETE',
        body: { currentPassword }
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const result = await queryFulfilled;

          if (result?.data?.status === 'success') {
            dispatch(logout());
            dispatch(apiSlice.util.resetApiState());
          }
        } catch (err) {
          logQueryError('deleteAccount failed', err);
        }
      },
      invalidatesTags: ['Profile']
    }),
    verifyOtp: builder.mutation({
      query: (data) => ({
        url: 'verify-otp',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['Profile']
    }),
    resendOtp: builder.mutation({
      query: (data) => ({
        url: 'resend-otp',
        method: 'POST',
        body: data
      })
    }),
    // Repoints a still-unverified personal email at a corrected address and
    // re-sends the code. Invalidates Profile because it changes user.email.
    changePendingEmail: builder.mutation({
      query: (data) => ({
        url: 'change-pending-email',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['Profile']
    })
  })
});

export const {
  // useGetProfileQuery,
  useLazyGetProfileQuery,
  useLazyGetReferralCodeQuery,
  useChangePasswordMutation,
  useResetPasswordMutation,
  useForgotPasswordMutation,
  useGetIndustryTypeQuery,
  useGetIndustryInfoQuery,
  useRegisterMutation,
  useVerifyWorkEmailMutation,
  useCompleteProfileMutation,
  useTryAgainCompleteProfileMutation,
  useLoginMutation,
  useLogoutMutation,
  useUpdateNewsletterStatusMutation,
  useDeleteAccountMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
  useChangePendingEmailMutation
} = authApi;
